const CONFIG = {
  selectors: {
    stickyCart: 'sticky-add-to-cart',
    drawer: 'drawer-element',
    widgetContainer: '#trengo-web-widget',
    widgetIframe: 'iframe[src*="trengo.eu"]',
  },
  urls: {
    script: 'https://static.widget.trengo.eu/embed.js',
  },
  attributes: {
    drawerState: 'aria-hidden'}};

class TrengoWidget extends HTMLElement {
  #updateTimer = null;

  connectedCallback() {
    const apiKey = this.dataset.id;
    if (!apiKey) return;

    this.initializeTrengo(apiKey);
    this.loadScript();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initDOMInteractions());
    } else {
      this.initDOMInteractions();
    }
  }

  disconnectedCallback() {
    if (this.cartObserver) {
      this.cartObserver.disconnect();
    }
    if (this.drawerObserver) {
      this.drawerObserver.disconnect();
    }
    if (this.widgetObserver) {
      this.widgetObserver.disconnect();
    }
    if (this.#updateTimer) cancelAnimationFrame(this.#updateTimer);
  }

  initializeTrengo(apiKey) {
    window.Trengo = window.Trengo || {};
    window.Trengo.key = apiKey;
  }

  loadScript() {
    if (document.querySelector(`script[src="${CONFIG.urls.script}"]`)) return;

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = CONFIG.urls.script;
    document.head.appendChild(script);
  }

  initDOMInteractions() {
    this.stickyCart = document.querySelector(CONFIG.selectors.stickyCart);
    this.drawers = document.querySelectorAll(CONFIG.selectors.drawer);

    this.widgetObserver = new MutationObserver(() => {
      const widgetElements = this.getWidgetElements();
      if (widgetElements.length > 0) {
        this.widgetObserver.disconnect();
        this.widgetObserver = null;
        this.cachedWidgetElements = widgetElements;
        this.scheduleUpdate();
      }
    });

    this.widgetObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    this.setupObservers();
    this.scheduleUpdate();
  }

  setupObservers() {
    const handleUpdate = () => this.scheduleUpdate();

    if (this.stickyCart) {
      this.cartObserver = new MutationObserver(handleUpdate);
      this.cartObserver.observe(this.stickyCart, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    if (this.drawers.length > 0) {
      this.drawerObserver = new MutationObserver(handleUpdate);
      this.drawers.forEach((drawer) => {
        this.drawerObserver.observe(drawer, {
          attributes: true,
          attributeFilter: [CONFIG.attributes.drawerState],
        });
      });
    }
  }

  scheduleUpdate() {
    if (this.#updateTimer) return;
    this.#updateTimer = requestAnimationFrame(() => {
      this.#updateTimer = null;
      this.updateWidgetState();
    });
  }

  updateWidgetState() {
    const widgetElements = this.cachedWidgetElements || this.getWidgetElements();

    if (!widgetElements.length && window.Trengo) {
      const offset = this.calculateOffset();
      window.Trengo.extraOffsetY = offset;
    }

    if (!widgetElements.length) return;

    const shouldHide = this.checkIfDrawersOpen();

    if (shouldHide) {
      this.toggleWidgetsVisibility(widgetElements, false);
    } else {
      this.toggleWidgetsVisibility(widgetElements, true);
      this.adjustWidgetPosition(widgetElements);
    }
  }

  checkIfDrawersOpen() {
    return Array.from(this.drawers).some(
      (drawer) => drawer.getAttribute(CONFIG.attributes.drawerState) === 'false'
    );
  }

  getWidgetElements() {
    const elements = [
      document.querySelector(CONFIG.selectors.widgetContainer),
      document.querySelector(CONFIG.selectors.widgetIframe),
    ];
    return elements.filter(Boolean);
  }

  toggleWidgetsVisibility(elements, isVisible) {
    const displayValue = isVisible ? '' : 'none';
    elements.forEach((el) => {
      if (el.style.display !== displayValue) {
        el.style.display = displayValue;
      }
    });
  }

  adjustWidgetPosition(elements) {
    const offset = this.calculateOffset();

    // Update global config if available
    if (window.Trengo) {
      window.Trengo.extraOffsetY = offset;
    }

    elements.forEach((el) => {
      el.style.setProperty('bottom', offset, 'important');
    });
  }

  calculateOffset() {
    const isStickyVisible = this.stickyCart && this.stickyCart.classList.contains('translate-y-0');

    if (isStickyVisible) {
      return `${this.stickyCart.offsetHeight}px`;
    }
    return '0px';
  }
}

customElements.define('trengo-widget', TrengoWidget);
